import apiClient from "./api";

/**
 * Customer credential type — see CustomerCredentialType.cs.
 * Mirrors the backend enum integer values: 0 None, 1 Pin, 2 Password.
 */
export type CustomerCredentialType = "None" | "Pin" | "Password";

export const customerCredentialTypeToNumber = (
  v?: CustomerCredentialType | null,
): number | null => {
  if (!v) return null;
  if (v === "None") return 0;
  if (v === "Pin") return 1;
  return 2;
};

export interface CustomerCompany {
  id: string;
  name: string;
  externalRef?: string | null;
  allowSelfRegistration: boolean;
  defaultCredentialType?: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  customerCount: number;
  customerAdminCount: number;
  lastActivityAt?: string | null;
}

export interface CustomerCompanyDetail extends CustomerCompany {
  agentInstruction?: string | null;
  /** Highest risk members may approve from Slack or Telegram: "low" | "medium" | "high" | "none"; null = everything. */
  chatApprovalCeiling?: string | null;
}

export interface CreateCustomerCompanyRequest {
  name: string;
  externalRef?: string | null;
  allowSelfRegistration?: boolean;
  defaultCredentialType?: number | null;
  agentInstruction?: string | null;
  isActive?: boolean;
}

export interface UpdateCustomerCompanyRequest {
  name?: string;
  externalRef?: string | null;
  allowSelfRegistration?: boolean;
  defaultCredentialType?: number | null;
  agentInstruction?: string | null;
  /** Omit to keep; "" clears it back to everything. */
  chatApprovalCeiling?: string | null;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface GetCustomerCompaniesParams {
  search?: string;
  includeInactive?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * Account-tier memory — mirrors backend MemoryDto.
 * A memory shared across every customer of a CustomerCompany (account scope).
 */
export interface AccountMemory {
  id: string;
  content: string;
  category: string;
  source: string;
  salience: number;
  status: string;
  scope: string;
  effectiveAt: string;
  expiresAt?: string | null;
  createdAt: string;
}

export interface CreateAccountMemoryRequest {
  content: string;
  category?: string;
  salience?: number;
}

export interface CustomerCompanyMember {
  id: string;
  customerId: string;
  customerName?: string | null;
  customerEmail?: string | null;
  /** Portal role: Admin can manage the company's own resources, Member is read-only. */
  role: "Admin" | "Member";
  /** Soft-disabled members cannot sign in; their history is retained. */
  isDisabled: boolean;
  /** Null when the member has never completed credential setup and cannot sign in yet. */
  credentialSetAt?: string | null;
  /** Free-text company identifier originally passed by the widget. Back-compat field —
   *  the typed FK is customerCompanyRefId. */
  customerCompanyId?: string | null;
  customerCompanyRefId?: string | null;
  customerCompanyName?: string | null;
  customerSummary?: string | null;
  companyId?: string;
  createdAt: string;
  lastUpdatedAt: string;
  hasPersona: boolean;
  personaLastAnalyzedAt?: string | null;
}

export const customerCompanyService = {
  getAll: async (
    params: GetCustomerCompaniesParams = {},
  ): Promise<PaginatedResult<CustomerCompany>> => {
    const response = await apiClient.get("/customer-companies", {
      params: {
        search: params.search || undefined,
        includeInactive: params.includeInactive ?? false,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
      },
    });
    return response.data;
  },

  getMembers: async (
    customerCompanyId: string,
    params: { search?: string; page?: number; pageSize?: number } = {},
  ): Promise<PaginatedResult<CustomerCompanyMember>> => {
    const response = await apiClient.get(
      `/customer-companies/${customerCompanyId}/members`,
      {
        params: {
          search: params.search || undefined,
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
        },
      },
    );
    return response.data;
  },

  getById: async (id: string): Promise<CustomerCompanyDetail> => {
    const response = await apiClient.get(`/customer-companies/${id}`);
    return response.data;
  },

  create: async (
    request: CreateCustomerCompanyRequest,
  ): Promise<CustomerCompanyDetail> => {
    const response = await apiClient.post("/customer-companies", request);
    return response.data;
  },

  update: async (
    id: string,
    request: UpdateCustomerCompanyRequest,
  ): Promise<CustomerCompanyDetail> => {
    const response = await apiClient.put(`/customer-companies/${id}`, request);
    return response.data;
  },

  toggleActive: async (
    id: string,
    isActive: boolean,
  ): Promise<CustomerCompanyDetail> => {
    const response = await apiClient.post(
      `/customer-companies/${id}/toggle-active`,
      { isActive },
    );
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/customer-companies/${id}`);
  },

  listAccountMemories: async (id: string): Promise<AccountMemory[]> => {
    const response = await apiClient.get(`/customer-companies/${id}/memories`);
    return response.data;
  },

  createAccountMemory: async (
    id: string,
    request: CreateAccountMemoryRequest,
  ): Promise<AccountMemory> => {
    const response = await apiClient.post(
      `/customer-companies/${id}/memories`,
      request,
    );
    return response.data;
  },

  deleteAccountMemory: async (id: string, memoryId: string): Promise<void> => {
    await apiClient.delete(`/customer-companies/${id}/memories/${memoryId}`);
  },
};
